/**
 * Reisafwijking-drempel: als de gereden km of reistijd meer dan 15% afwijkt van de
 * berekende waarde én de absolute delta ≥ 30 min of ≥ 5 km is, moet de monteur een
 * reden opgeven. Gedeeld tussen API (validatie) en portal (UI), zodat er geen drift
 * kan ontstaan tussen wat de UI toont en wat de server accepteert.
 */

export const VARIANCE_PERCENT_THRESHOLD = 0.15 // 15%
export const VARIANCE_MIN_MINUTES = 30
export const VARIANCE_MIN_KM = 5

export interface VarianceInput {
  km: number
  minuten: number
  kmBerekend: number | null
  minutenBerekend: number | null
}

export function requiresVarianceReason(input: VarianceInput): boolean {
  const { km, minuten, kmBerekend, minutenBerekend } = input

  if (kmBerekend == null || minutenBerekend == null) return false
  if (kmBerekend === 0 && minutenBerekend === 0) return false

  const kmTriggers = exceedsThresholds(km, kmBerekend, VARIANCE_MIN_KM)
  const minTriggers = exceedsThresholds(minuten, minutenBerekend, VARIANCE_MIN_MINUTES)

  return kmTriggers || minTriggers
}

function exceedsThresholds(gereden: number, berekend: number, absMin: number): boolean {
  if (berekend === 0) return false
  const delta = Math.abs(gereden - berekend)
  const ratio = delta / berekend
  return ratio > VARIANCE_PERCENT_THRESHOLD && delta >= absMin
}

/**
 * Reisblok-afwijking-signaal voor de monteur-UI. PURE PRESENTATIE: het toont of er
 * een afwijking op de reistijd is ingediend/goedgekeurd/afgewezen, plus het verschil
 * tussen werkelijke en berekende reistijd in minuten (afgerond op 15). De
 * `afwijkingStatus` komt als platte string binnen (geen import uit @streep/types).
 *
 * `ReisUrenBerekeningDTO` levert `berekendeReistijdUren` en `werkelijkeReistijdUren`
 * (nullable); `afwijkingStatus` is een van INGEDIEND/GOEDGEKEURD/AFGEWEZEN/
 * NIET_VAN_TOEPASSING/null.
 */
export type ReisAfwijkingSignaalKind = 'GEEN' | 'AFWIJKING' | 'AFGEWEZEN'

export interface ReisAfwijkingSignaalInput {
  afwijkingStatus: string | null
  berekendeReistijdUren: number
  werkelijkeReistijdUren: number | null
}

export interface ReisAfwijkingSignaal {
  kind: ReisAfwijkingSignaalKind
  deltaMinuten: number | null
}

/** Rondt het verschil (werkelijke - berekende) in minuten af op de dichtstbijzijnde 15. */
function reisDeltaMinuten(berekendeUren: number, werkelijkeUren: number | null): number | null {
  if (werkelijkeUren === null) return null
  const rawMinutes = (werkelijkeUren - berekendeUren) * 60
  return Math.round(rawMinutes / 15) * 15
}

export function deriveReisAfwijkingSignaal(input: ReisAfwijkingSignaalInput): ReisAfwijkingSignaal {
  const delta = reisDeltaMinuten(input.berekendeReistijdUren, input.werkelijkeReistijdUren)

  if (input.afwijkingStatus === 'INGEDIEND' || input.afwijkingStatus === 'GOEDGEKEURD') {
    // Zonder werkelijke tijd valt er niets te tonen → terug naar GEEN.
    if (delta === null) return { kind: 'GEEN', deltaMinuten: null }
    return { kind: 'AFWIJKING', deltaMinuten: delta }
  }

  if (input.afwijkingStatus === 'AFGEWEZEN') {
    return { kind: 'AFGEWEZEN', deltaMinuten: delta }
  }

  // null, NIET_VAN_TOEPASSING of iets anders → geen signaal.
  return { kind: 'GEEN', deltaMinuten: null }
}

/**
 * Formatteert een (getekend) reis-delta in minuten voor de UI.
 * 0 → "0 min"; |d| < 60 → "+15 min" / "-10 min"; |d| >= 60 → "+1:30" / "-1:30"
 * (minuten zero-padded op 2). ASCII '+'/'-', geen em-dash.
 */
export function formatReisDeltaMinuten(deltaMinuten: number): string {
  if (deltaMinuten === 0) return '0 min'

  const sign = deltaMinuten > 0 ? '+' : '-'
  const abs = Math.abs(deltaMinuten)

  if (abs < 60) {
    return `${sign}${abs} min`
  }

  const hours = Math.floor(abs / 60)
  const minutes = abs % 60
  return `${sign}${hours}:${String(minutes).padStart(2, '0')}`
}
